import React, { useState, useEffect } from "react";
import {
    LineChart, Line, BarChart, Bar, AreaChart, Area,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import styles from "../styles/AdminCharts.module.css";
import { getApiUrl } from "../utils/config";

const API_URL = getApiUrl();

const AdminCharts = ({ bonusesStats, bonusesStatsLoading, selectedCurrency, setSelectedCurrency, userStats, financeStats }) => {
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [period, setPeriod] = useState('all'); // По умолчанию "За всё время"
    
    // Функция для получения символа валюты
    const getCurrencySymbol = (currency) => {
        const symbols = {
            'RUB': '₽',
            'USD': '$',
            'BRL': 'R$',
            'USDT': '₮',
            'EUR': '€'
        };
        return symbols[currency] || currency;
    };

    useEffect(() => {
        fetchChartData();
    }, [period, selectedCurrency]);

    const fetchChartData = async () => {
        setLoading(true);
        setError("");

        try {
            const to = new Date();
            const from = new Date();
            
            if (period === 'all') {
                from.setFullYear(2020, 0, 1); // С 1 января 2020 года
            } else {
                from.setDate(from.getDate() - parseInt(period));
            }

            const params = new URLSearchParams({
                from: from.toISOString(),
                to: to.toISOString(),
                currency: selectedCurrency
            });

            const res = await fetch(`${API_URL}/api/admin/chart-stats?${params}`, {
                credentials: "include"
            });

            const data = await res.json();

            if (data.success) {
                // Форматируем даты для отображения
                const formattedData = data.data.map(item => ({
                    ...item,
                    dateFormatted: new Date(item.date).toLocaleDateString('ru-RU', {
                        day: '2-digit',
                        month: '2-digit'
                    })
                }));
                setChartData(formattedData);
            } else {
                setError(data.message || "Ошибка загрузки данных");
            }
        } catch (err) {
            setError("Ошибка соединения с сервером");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Кастомный тултип в тёмной теме
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className={styles.customTooltip}>
                    <p className={styles.tooltipLabel}>{label}</p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{ color: entry.color }}>
                            {entry.name}: <strong>{typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}</strong>
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    if (loading) {
        return (
            <div className={styles.chartsContainer}>
                <div className={styles.loading}>⏳ Загрузка данных...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.chartsContainer}>
                <div className={styles.error}>❌ {error}</div>
            </div>
        );
    }

    return (
        <div className={styles.chartsContainer}>
            <div className={styles.header}>
                <h2>📊 Общая статистика</h2>
                <div className={styles.filtersContainer}>
                    <div className={styles.periodSelector}>
                        <label>Валюта:</label>
                        <select 
                            value={selectedCurrency} 
                            onChange={(e) => setSelectedCurrency(e.target.value)}
                            className={styles.periodSelect}
                        >
                            <option value="RUB">₽ RUB</option>
                            <option value="USD">$ USD</option>
                            <option value="BRL">R$ BRL</option>
                            <option value="USDT">₮ USDT</option>
                            <option value="EUR">€ EUR</option>
                        </select>
                    </div>
                    <div className={styles.periodSelector}>
                        <label>Период:</label>
                        <select 
                            value={period} 
                            onChange={(e) => setPeriod(e.target.value)}
                            className={styles.periodSelect}
                        >
                            <option value="all">За всё время</option>
                            <option value="7">7 дней</option>
                            <option value="14">14 дней</option>
                            <option value="30">30 дней (1 месяц)</option>
                            <option value="60">60 дней (2 месяца)</option>
                            <option value="90">90 дней (3 месяца)</option>
                            <option value="180">180 дней (6 месяцев)</option>
                            <option value="365">365 дней (1 год)</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Итоги за выбранный период */}
            <div className={styles.summaryBlock}>
                <h3>📈 Итоги за выбранный период ({getCurrencySymbol(selectedCurrency)} {selectedCurrency})</h3>
                <div className={styles.summaryGrid}>
                    <div className={styles.summaryCard}>
                        <div className={styles.summaryIcon}>👥</div>
                        <div className={styles.summaryLabel}>Всего регистраций</div>
                        <div className={styles.summaryValue}>
                            {chartData.reduce((sum, item) => sum + item.registrations, 0)}
                        </div>
                    </div>
                    <div className={styles.summaryCard}>
                        <div className={styles.summaryIcon}>💳</div>
                        <div className={styles.summaryLabel}>Всего платежей</div>
                        <div className={styles.summaryValue}>
                            {chartData.reduce((sum, item) => sum + item.allPaymentsCount, 0)}
                        </div>
                    </div>
                    <div className={styles.summaryCard}>
                        <div className={styles.summaryIcon}>✅</div>
                        <div className={styles.summaryLabel}>Успешных платежей</div>
                        <div className={styles.summaryValue}>
                            {chartData.reduce((sum, item) => sum + item.successfulPaymentsCount, 0)}
                        </div>
                    </div>
                    <div className={styles.summaryCard}>
                        <div className={styles.summaryIcon}>📈</div>
                        <div className={styles.summaryLabel}>Конверсия платежей</div>
                        <div className={styles.summaryValue}>
                            {(() => {
                                const total = chartData.reduce((sum, item) => sum + item.allPaymentsCount, 0);
                                const successful = chartData.reduce((sum, item) => sum + item.successfulPaymentsCount, 0);
                                return total > 0 ? ((successful / total) * 100).toFixed(1) + '%' : '0%';
                            })()}
                        </div>
                    </div>
                    <div className={styles.summaryCard}>
                        <div className={styles.summaryIcon}>💰</div>
                        <div className={styles.summaryLabel}>Сумма депозитов</div>
                        <div className={styles.summaryValue}>
                            {chartData.reduce((sum, item) => sum + item.depositsSum, 0).toFixed(2)} {getCurrencySymbol(selectedCurrency)}
                        </div>
                    </div>
                    <div className={styles.summaryCard}>
                        <div className={styles.summaryIcon}>💸</div>
                        <div className={styles.summaryLabel}>Сумма выводов</div>
                        <div className={styles.summaryValue}>
                            {chartData.reduce((sum, item) => sum + item.withdrawalsSum, 0).toFixed(2)} {getCurrencySymbol(selectedCurrency)}
                        </div>
                    </div>
                    <div className={styles.summaryCard}>
                        <div className={styles.summaryIcon}>📊</div>
                        <div className={styles.summaryLabel}>Чистая прибыль</div>
                        <div className={styles.summaryValue}>
                            {(chartData.reduce((sum, item) => sum + item.depositsSum, 0) - 
                              chartData.reduce((sum, item) => sum + item.withdrawalsSum, 0)).toFixed(2)} {getCurrencySymbol(selectedCurrency)}
                        </div>
                    </div>
                    <div className={styles.summaryCard}>
                        <div className={styles.summaryIcon}>💵</div>
                        <div className={styles.summaryLabel}>Средний чек депозита</div>
                        <div className={styles.summaryValue}>
                            {(() => {
                                const total = chartData.reduce((sum, item) => sum + item.successfulPaymentsCount, 0);
                                const sum = chartData.reduce((sum, item) => sum + item.depositsSum, 0);
                                return total > 0 ? (sum / total).toFixed(2) + ' ' + getCurrencySymbol(selectedCurrency) : '0 ' + getCurrencySymbol(selectedCurrency);
                            })()}
                        </div>
                    </div>
                </div>
            </div>

            {/* Статистика бонусов по выбранной валюте */}
            {bonusesStats && !bonusesStatsLoading && (
                <div className={styles.summaryBlock}>
                    <h3>🎁 Статистика бонусов и кешбеков ({getCurrencySymbol(selectedCurrency)} {selectedCurrency})</h3>
                    <div className={styles.summaryGrid}>
                        {/* Рейкбек по валюте */}
                        {(() => {
                            const rakebackData = bonusesStats.rakebackByCurrency?.find(item => item.currency === selectedCurrency);
                            return (
                                <div className={styles.summaryCard}>
                                    <div className={styles.summaryIcon}>🎯</div>
                                    <div className={styles.summaryLabel}>Рейкбек</div>
                                    <div className={styles.summaryValue}>
                                        {Number(rakebackData?.total_amount || 0).toFixed(2)} {getCurrencySymbol(selectedCurrency)}
                                    </div>
                                    <div className={styles.summarySubLabel}>
                                        {rakebackData?.total_transactions || 0} транзакций
                                    </div>
                                </div>
                            );
                        })()}
                        
                        {/* Выводы с бонусного счёта по валюте */}
                        {(() => {
                            const bonusWithdrawData = bonusesStats.bonusWithdrawByCurrency?.find(item => item.currency === selectedCurrency);
                            return (
                                <div className={styles.summaryCard}>
                                    <div className={styles.summaryIcon}>💰</div>
                                    <div className={styles.summaryLabel}>Выводы с бонусного счёта</div>
                                    <div className={styles.summaryValue}>
                                        {Number(bonusWithdrawData?.total_amount || 0).toFixed(2)} {getCurrencySymbol(selectedCurrency)}
                                    </div>
                                    <div className={styles.summarySubLabel}>
                                        {bonusWithdrawData?.total_transactions || 0} транзакций
                                    </div>
                                </div>
                            );
                        })()}
                        
                        {/* Списания с бонусного счёта по валюте */}
                        {(() => {
                            const bonusSpentData = bonusesStats.bonusSpentByCurrency?.find(item => item.currency === selectedCurrency);
                            return (
                                <div className={styles.summaryCard}>
                                    <div className={styles.summaryIcon}>🔥</div>
                                    <div className={styles.summaryLabel}>Списания с бонусного счёта</div>
                                    <div className={styles.summaryValue}>
                                        {Number(bonusSpentData?.total_amount || 0).toFixed(2)} {getCurrencySymbol(selectedCurrency)}
                                    </div>
                                    <div className={styles.summarySubLabel}>
                                        {bonusSpentData?.total_transactions || 0} транзакций
                                    </div>
                                </div>
                            );
                        })()}
                        
                        {/* Кешбек из транзакций по валюте */}
                        {(() => {
                            const cashbackData = bonusesStats.cashbackFromTransactionsByCurrency?.find(item => item.currency === selectedCurrency);
                            return (
                                <div className={styles.summaryCard}>
                                    <div className={styles.summaryIcon}>📊</div>
                                    <div className={styles.summaryLabel}>Кешбек (из транзакций)</div>
                                    <div className={styles.summaryValue}>
                                        {Number(cashbackData?.total_amount || 0).toFixed(2)} {getCurrencySymbol(selectedCurrency)}
                                    </div>
                                    <div className={styles.summarySubLabel}>
                                        {cashbackData?.total_transactions || 0} транзакций
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Еженедельный кешбек (общий для всех валют) */}
                        <div className={styles.summaryCard}>
                            <div className={styles.summaryIcon}>📅</div>
                            <div className={styles.summaryLabel}>Еженедельный кешбек</div>
                            <div className={styles.summaryValue}>
                                {Number(bonusesStats.weeklyCashback?.total_cashback || 0).toFixed(2)} ₽
                            </div>
                            <div className={styles.summarySubLabel}>
                                {bonusesStats.weeklyCashback?.total_users || 0} пользователей (все валюты)
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* График регистраций */}
            <div className={styles.chartBlock}>
                <h3>👥 Регистрации пользователей</h3>
                <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={chartData}>
                        <defs>
                            <linearGradient id="colorRegistrations" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8}/>
                                <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis 
                            dataKey="dateFormatted" 
                            stroke="#9ca3af"
                            style={{ fontSize: '12px' }}
                        />
                        <YAxis 
                            stroke="#9ca3af"
                            style={{ fontSize: '12px' }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Area 
                            type="monotone" 
                            dataKey="registrations" 
                            stroke="#8b5cf6" 
                            fillOpacity={1} 
                            fill="url(#colorRegistrations)"
                            name="Регистрации"
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>

            {/* График платежей (количество) */}
            <div className={styles.chartBlock}>
                <h3>💳 Платежи (количество)</h3>
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis 
                            dataKey="dateFormatted" 
                            stroke="#9ca3af"
                            style={{ fontSize: '12px' }}
                        />
                        <YAxis 
                            stroke="#9ca3af"
                            style={{ fontSize: '12px' }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend 
                            wrapperStyle={{ color: '#d1d5db' }}
                        />
                        <Bar 
                            dataKey="allPaymentsCount" 
                            fill="#f59e0b" 
                            name="Все платежи"
                        />
                        <Bar 
                            dataKey="successfulPaymentsCount" 
                            fill="#10b981" 
                            name="Успешные платежи"
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>

            {/* График финансов (суммы) */}
            <div className={styles.chartBlock}>
                <h3>💰 Финансовые потоки (суммы)</h3>
                <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis 
                            dataKey="dateFormatted" 
                            stroke="#9ca3af"
                            style={{ fontSize: '12px' }}
                        />
                        <YAxis 
                            stroke="#9ca3af"
                            style={{ fontSize: '12px' }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend 
                            wrapperStyle={{ color: '#d1d5db' }}
                        />
                        <Line 
                            type="monotone" 
                            dataKey="depositsSum" 
                            stroke="#10b981" 
                            strokeWidth={2}
                            dot={{ fill: '#10b981', r: 4 }}
                            name="Депозиты"
                        />
                        <Line 
                            type="monotone" 
                            dataKey="withdrawalsSum" 
                            stroke="#ef4444" 
                            strokeWidth={2}
                            dot={{ fill: '#ef4444', r: 4 }}
                            name="Выводы"
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            {/* Комбинированный график: Платежи (сумма) */}
            <div className={styles.chartBlock}>
                <h3>💵 Платежи (суммы)</h3>
                <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={chartData}>
                        <defs>
                            <linearGradient id="colorAllPayments" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.8}/>
                                <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id="colorSuccessPayments" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                                <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis 
                            dataKey="dateFormatted" 
                            stroke="#9ca3af"
                            style={{ fontSize: '12px' }}
                        />
                        <YAxis 
                            stroke="#9ca3af"
                            style={{ fontSize: '12px' }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend 
                            wrapperStyle={{ color: '#d1d5db' }}
                        />
                        <Area 
                            type="monotone" 
                            dataKey="allPaymentsSum" 
                            stroke="#f59e0b" 
                            fillOpacity={1} 
                            fill="url(#colorAllPayments)"
                            name="Все платежи"
                        />
                        <Area 
                            type="monotone" 
                            dataKey="successfulPaymentsSum" 
                            stroke="#10b981" 
                            fillOpacity={1} 
                            fill="url(#colorSuccessPayments)"
                            name="Успешные платежи"
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>

        </div>
    );
};

export default AdminCharts;

